import { Window } from './window';

export enum AxisType {
    Left   = 'left',
    Right  = 'right',
    Bottom = 'bottom'
}

interface Magnetude {
    exponent: number;
    divider: number;
}

export class Axis extends Window
{
    private minValue   : number;
    private rangeValue : number;
    private margin     : number;
    public  desc       : string;
    public  type       : AxisType;
    public  unit       : string;
    public  ticks      : number[];
    public  stepSize   : number | undefined;

    constructor(type : AxisType, margin : number) 
    {
        super();

        this.margin     = margin;
        this.minValue   = 0;
        this.rangeValue = 0;
        this.type       = type;
        this.unit       = "";
        this.desc       = "";
        this.ticks      = [];
    }

    public get lengthPixels() : number
    {
        if (this.type === AxisType.Bottom) {
            return this.width - this.margin *2;
        }
        else {
            return -(this.height - this.margin * 2);
        }
    }

    public get startPixels() : number
    {
        if (this.type === AxisType.Bottom) {
            return this.cx + this.margin;
        }
        else {
            return this.height - this.margin;
        }
    }

    public calcMagnetude(x: number): Magnetude {
        var exp = 1;
        var div = 1;

        if (x === 0) {
            return {
                exponent: 0,
                divider: 1,
            };
        }

        if (x < 0) {
            x = -x;
        }

        while (x < 1 || x >= 10) {
            if (x < 1) {
                exp--;
                x *= 10;
                div /= 10;
            }

            if (x >= 10) {
                exp++;
                x /= 10;
                div *= 10;
            }
        }

        return {
            exponent: exp,
            divider: div,
        };
    }

    private calcStepSize() : number
    {
        let stepSize = this.calcAutoStepSize();
        if (this.stepSize !== undefined && stepSize < this.stepSize) {
            return this.stepSize;
        }
        return stepSize;
    }

    private calcAutoStepSize() : number
    {

        // Get range in graph coordinates
        var range = this.rangeValue - this.minValue;
        if (typeof range == 'undefined' || range < 0.0001) {
            return 0;
        }

        let lengthPixels = Math.abs(this.lengthPixels);

        // Get length of Axis in Pixels
        if (lengthPixels < 10) {
            return 0;
        }

        const minGridSize = 20;
        let   stepSize    = 1;

        // Fine tune to the nearest 10
        while (true) {
            const gridSizePixels = (lengthPixels * stepSize) / range;

            if (gridSizePixels * 10 < minGridSize) {
                stepSize *= 10;
            } else if (gridSizePixels > minGridSize) {
                stepSize /= 10;
            } else {
                break;
            }
        }

        while (true) {
            const gridSizePixels = (lengthPixels * stepSize) / range;
            if (gridSizePixels > minGridSize) {
                break;
            } else if (gridSizePixels * 2 >= minGridSize) {
                stepSize *= 2;
                break;
            } else if (gridSizePixels * 5 >= minGridSize) {
                stepSize *= 5;
                break;
            }

            stepSize *= 10;
        }

        return stepSize;
    }

    draw(ctx : CanvasRenderingContext2D) 
    {
        ctx.setLineDash([]);
        ctx.strokeStyle = "black";
        ctx.lineWidth   = 1;

        switch(this.type) {
            case AxisType.Left :
                this.drawLeft(ctx);
                break;
            case AxisType.Right :
                this.drawRight(ctx);
                break;
            case AxisType.Bottom :
                this.drawBottom(ctx);
                break;
        }

        this.drawDescription(ctx);

        if (this.rangeValue === 0.0) {
            return;
        }

        let stepSize = this.calcStepSize();
        if (stepSize === 0) {
            return;
        }

        var x = Math.floor(this.minValue / stepSize) * stepSize
        this.ticks = [];

        while(true) {
            if(x > Math.ceil(this.minValue + this.rangeValue)) {
                break;
            }

            // Draw the lines
            var xInPixels = this.valueToPixel(x);
            this.drawMark(ctx, xInPixels);

            // Add Grid Line
            this.ticks.push(xInPixels);

            // Draw Ruler Text
            let labelText = this.makeLabelText(x, stepSize);
            this.drawLabel(ctx, xInPixels, labelText);

            x += stepSize;
        }
    }

    private drawDescription(ctx : CanvasRenderingContext2D)
    {
        if (this.desc.length === 0) {
            return;
        }

        ctx.font      = '12px Open Sans';
        ctx.fillStyle = "black";
        const metrics = ctx.measureText(this.desc);

        if (this.type === AxisType.Bottom) {
            ctx.translate(this.cx + (this.width - metrics.width)/2, this.cy + 34);
        }
        else {
            let x = 0;
            if (this.type === AxisType.Left) {
                x = this.cx + 12;
            }
            else if (this.type === AxisType.Right) {
                x = this.cx + this.width - 12;
            }

            ctx.translate(x, this.cy + this.height/2 + metrics.width/2);
            ctx.rotate(-Math.PI / 2);
        }

        ctx.fillText(this.desc, 0, 0);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    protected drawLabel(ctx : CanvasRenderingContext2D, pos : number, label : string)
    {
        const metrics = ctx.measureText(label);
        const height  = metrics.actualBoundingBoxAscent;

        ctx.fillStyle = "black";
        ctx.font      = '12px Open Sans';

        switch (this.type) {
            case AxisType.Left:
                ctx.fillText(label, this.cx + this.width - metrics.width - 12, pos + height / 2);
                break;

            case AxisType.Right:
                ctx.fillText(label, this.cx + metrics.width - 8, pos + height / 2);
                break;

            case AxisType.Bottom:
                ctx.fillText(label, pos - metrics.width/2, this.cy + 10 + height);
                break;
        }
    }

    protected drawMark(ctx : CanvasRenderingContext2D, pos: number) 
    {
        ctx.lineWidth = 1

        switch (this.type) {
            case AxisType.Left:
                var right = this.width;
                ctx.beginPath();
                ctx.moveTo(right - 1, pos);
                ctx.lineTo(right - 6, pos);
                ctx.stroke();
                break;

            case AxisType.Right:
                ctx.beginPath();
                ctx.moveTo(this.cx + 1, pos);
                ctx.lineTo(this.cx + 6, pos);
                ctx.stroke();
                break;

            case AxisType.Bottom:
                ctx.beginPath();
                ctx.moveTo(pos, this.cy + 1);
                ctx.lineTo(pos, this.cy + 6);
                ctx.stroke();
                break;
        }
    }


    drawLeft(ctx : CanvasRenderingContext2D) {
        ctx.beginPath();
        ctx.moveTo(this.cx + this.width, this.cy);
        ctx.lineTo(this.cx + this.width, this.cy + this.height);
        ctx.stroke();
    }

    drawRight(ctx : CanvasRenderingContext2D) {
        ctx.beginPath();
        ctx.moveTo(this.cx, this.cy);
        ctx.lineTo(this.cx, this.cy + this.height);
        ctx.stroke();
    }

    drawBottom(ctx : CanvasRenderingContext2D) {
        ctx.beginPath();
        ctx.moveTo(this.cx, this.cy);
        ctx.lineTo(this.cx + this.width, this.cy);
        ctx.stroke();
    }

    protected makeLabelText(x: number, stepSize: number): string {
        var label = '';
        stepSize = Math.abs(stepSize);

        if (x < 0) {
            x = -x;
            label = '-';
        }

        // Round number
        var noSteps = Math.floor((x + stepSize / 2) / stepSize);
        x = stepSize * noSteps;

        // Determine numerical precsion
        var expStep = this.calcMagnetude(stepSize);
        var expNum = this.calcMagnetude(x);
        var div = expNum.divider;

        // Print leading zeros
        if (expNum.exponent < 0) {
            label += '0.';
            for (let i = 0; i < -expStep.exponent; i++) {
                label += '0';
            }
        }

        if (expNum.exponent === 0) {
            label += '0';
        }

        if (expNum.exponent >= expStep.exponent) {
            // Print precision numbers
            for (let i = expNum.exponent; i >= expStep.exponent; i--) {
                if (i === 0) {
                    label += '.';
                }

                var liDigit = Math.floor(x / div);
                label += String.fromCharCode(liDigit + 48);
                x -= liDigit * div;
                div /= 10;
            }

            // fill in zeros
            for (let i = expStep.exponent - 1; i > 0; i--) {
                label += '0';
            }
        }

        return label + this.unit;
    }

    valueToPixel(value : number) : number
    {
        const rel   = (value - this.minValue) / this.rangeValue;
        const pixel = rel * this.lengthPixels + this.startPixels;

        return Math.round(pixel);
    }

    setRange(min : number, max : number) {
        this.minValue = min;
        this.rangeValue = max - min;
    }
}

